import styled from '@emotion/styled';
import {
  type AllowedColors,
  Button,
  colors,
  Text,
  Tooltip
} from '@innovationdepartment/proxima-ui';
import { TableRows, ViewModule } from '@mui/icons-material';
import LoadingBar from 'ui/LoadingBar';
import type { BrandProductsViewProps } from './types';

const ToolbarContainer = styled.div`
  margin-top: -22px;
  display: flex;
  justify-content: space-between;
`;

const ToggleButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const ToggleButton = styled.div<{ selected?: boolean }>`
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.neutral0};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  ${({ selected }) => {
    if (!selected) return '';

    return `
      background: ${colors.neutral10};
      border: 1px solid ${colors.neutral20};
    `;
  }}

  & svg {
    color: ${colors.neutral60};
  }

  &:hover {
    background: ${colors.neutral5};
    border: 1px solid ${colors.neutral10};
  }
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 12px;
`;

const ClearSelectedButton = styled(Button)`
  &:hover {
    background-color: ${colors.red60};
  }

  margin-right: 8px;
`;

const ChangeTaxonomyButton = styled(Button)`
  &:hover {
    background-color: ${colors.green70};
  }

  margin-right: 32px;
`;

const BrandProductsToolbar = (props: BrandProductsViewProps) => {
  const {
    total,
    view,
    onViewChange,
    loading,
    selectedProducts,
    displayed,
    onClearSelectedProducts,
    onClassificationClick
  } = props;

  return (
    <ToolbarContainer>
      <LoadingBar loading={loading} />
      <TextWrapper>
        {!!total && (
          <Text variant="body3Semibold">{`Total Products: ${displayed} out of ${total}`}</Text>
        )}
      </TextWrapper>
      <ToggleButtonsWrapper>
        {selectedProducts.length > 0 && (
          <>
            <ClearSelectedButton
              overrideColors={{
                backgroundColor: colors.red30,
                textColor: colors.white as AllowedColors
              }}
              label="Clear selection"
              onClick={onClearSelectedProducts}
            />
            <ChangeTaxonomyButton
              overrideColors={{
                backgroundColor: colors.green40,
                textColor: colors.white as AllowedColors
              }}
              label="Change Taxonomy"
              onClick={onClassificationClick}
            />
          </>
        )}
        <TextWrapper>
          <Text variant="body3Semibold">Change view</Text>
        </TextWrapper>
        <Tooltip title="Table View">
          <ToggleButton selected={view === 'table'} onClick={() => onViewChange('table')}>
            <TableRows />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Card View">
          <ToggleButton selected={view === 'cards'} onClick={() => onViewChange('cards')}>
            <ViewModule />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonsWrapper>
    </ToolbarContainer>
  );
};

export default BrandProductsToolbar;
